/* eslint-disable no-mixed-spaces-and-tabs */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import BASE_QUERY from '@/features/baseQuery';
import { clientsApi } from '../clients/clientsApi';
import { triggerRefresh } from '../table/clientsTableSlice';

// Creating an API service using RTK Query
export const contactsApi = createApi({
	reducerPath: 'contactsApi',
	tagTypes: ['CONTACTS', 'CONTACT', 'CONTACT_CLIENT'],
	baseQuery: fetchBaseQuery({ ...BASE_QUERY, baseUrl: process.env.REACT_APP_CONTACT_API }),
	endpoints: (builder) => ({
		// Defining an endpoint to get properties of a specific contact
		//
		// new CONTACT API endpoints
		//
		getContact: builder.query({
			providesTags: (result, error, insightId) => [{ type: 'CONTACT', id: insightId }],
			query: (insightId) => ({
				url: '/get',
				method: 'POST',
				body: {
					insightId,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform /get response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform /get response', response);
				return response;
			},
		}),
		listContacts: builder.query({
			providesTags: (result, error, clientId) => [{ type: 'CONTACTS', id: clientId }],
			query: (clientId) => ({
				url: '/list', // we would move this url to the base query eventually and just use /create here
				method: 'POST',
				body: {
					clientId,
				},
			}),
			transformResponse: (response, meta, clientId) => {
				console.log('👾 ~ transform /list response', { response, meta, clientId });
				const mappedData = response?.map((r) => ({
					...r,
					isMainContact: r?.IsPrimary,
					clientId,
				}));

				return mappedData;
			},
		}),
		createContact: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'CLIENTS', id: 'LIST' },
				{ type: 'CONTACT_CLIENT', id: arg.clientId },
			],
			query: ({ clientId, ...params }) => ({
				url: '/create',
				method: 'POST',
				body: {
					clientId,
					...params,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform /create response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform /create response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					const res = await queryFulfilled;

					dispatch(
						clientsApi.util.invalidateTags([{ type: 'CLIENT', id: arg.clientId }]),
					);
					try {
						const patchResult = dispatch(
							contactsApi.util.updateQueryData(
								'listContacts',
								arg.clientId,
								(draft) => {
									if (res.data.Contact.IsPrimary) {
										// update the other contacts
										draft.forEach((c) => {
											c.IsPrimary = false;
											c.isMainContact = false;
										});
									}
									draft.push({
										...res.data.Contact,
										isMainContact: res.data.Contact.IsPrimary,
										clientId: arg.clientId,
									});
								},
							),
						);
					} catch (e) {
						console.error('Error patching contacts list', e);
						dispatch(
							contactsApi.util.invalidateTags([
								{ type: 'CONTACTS', id: arg.clientId },
							]),
						);
					}

					setTimeout(() => {
						dispatch(triggerRefresh());
					}, 10000);
				} catch (error) {
					//
				}
			},
		}),
		updateContact: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'CLIENTS', id: 'LIST' },
				{ type: 'CONTACTS', id: arg.clientId },
				{ type: 'CONTACT_CLIENT', id: arg.clientId },
				{ type: 'CONTACTS', id: 'LIST' },
				{ type: 'CONTACT', id: arg.insightId },
				'CONTACTS',
			],
			query: ({ insightId, clientId, ...params }) => ({
				url: '/update',
				method: 'POST',
				body: {
					insightId,
					clientId,
					...params,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform /update response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform /update response', response);
				return response;
			},
			onQueryStarted: async (
				{ insightId, clientId, ...params },
				{ dispatch, queryFulfilled },
			) => {
				console.log('[updateContact] args', { insightId, clientId, ...params });
				const patchResult = dispatch(
					contactsApi.util.updateQueryData('listContacts', clientId, (draft) => {
						const index = draft.findIndex((v) => v.Id === insightId);
						if (index !== -1) {
							draft[index] = {
								...draft[index],
								...params,
								...(params.isMainContact != null && {
									IsPrimary: params.isMainContact,
								}),
								...(params.IsPrimary != null && {
									isMainContact: params.IsPrimary,
								}),
							};
						}
					}),
				);
				const getPatchResult = dispatch(
					contactsApi.util.updateQueryData('getContact', insightId, (draft) => {
						Object.entries(params).forEach(([k, v]) => {
							draft[k] = v;
							if (['IsPrimary', 'isMainContact'].includes(k)) {
								draft.IsPrimary = v;
								draft.isMainContact = v;
							}
						});
					}),
				);
				try {
					await queryFulfilled;
					dispatch(clientsApi.util.invalidateTags([{ type: 'CLIENT', id: clientId }]));
					setTimeout(() => {
						dispatch(triggerRefresh());
					}, 10000);
					if (params.IsPrimary != null || params.isMainContact != null) {
						dispatch(
							clientsApi.util.invalidateTags([
								{ type: 'CONTACT' },
								{ type: 'CONTACTS', id: clientId },
							]),
						);
					}
				} catch (error) {
					patchResult.undo();
					getPatchResult.undo();
				}
			},
		}),
		deleteContact: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'CLIENTS', id: 'LIST' },
				{ type: 'CONTACT', id: arg.insightId },
				{ type: 'CONTACT_CLIENT', id: arg.clientId },
			],
			query: ({ clientId, insightId, ...params }) => ({
				url: '/delete',
				method: 'POST',
				body: {
					clientId,
					insightId,
					...params,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform /delete response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform /delete response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				console.log('[updateContact] args', arg);
				const patchResult = dispatch(
					contactsApi.util.updateQueryData('listContacts', arg.clientId, (draft) => {
						return draft.filter((v) => v.Id !== arg.insightId);
					}),
				);
				try {
					await queryFulfilled;
					dispatch(
						clientsApi.util.invalidateTags([{ type: 'CLIENT', id: arg.clientId }]),
					);
					setTimeout(() => {
						dispatch(triggerRefresh());
					}, 10000);
				} catch (error) {
					patchResult.undo();
					dispatch(
						contactsApi.util.invalidateTags([{ type: 'CONTACTS', id: arg.clientId }]),
					);
				}
			},
		}),
		searchContacts: builder.infiniteQuery({
			providesTags: ['CONTACTS'],
			infiniteQueryOptions: {
				initialPageParam: {
					PageNumber: 1,
					RowsPerPage: 100,
				},
				getNextPageParam: (lastPage, _allPages, lastPageParam) => {
					const nextPage = lastPageParam.PageNumber + 1;
					const totalPages = lastPage?.PageCount ?? 0;
					if (nextPage > totalPages) return undefined;
					return { ...lastPageParam, PageNumber: nextPage };
				},
				getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
					const prevPage = firstPageParam.PageNumber - 1;
					if (prevPage <= 0) return undefined;
					return { ...firstPageParam, PageNumber: prevPage };
				},
			},
			queryFn: async ({ pageParam, queryArg }, _api, _extraOptions, baseQuery) => {
				const { PageNumber, RowsPerPage } = pageParam;
				const {
					searchText,
					clientId,
					ownerDocIds = [],
					brokerGroupId,
					isPrimary,
				} = queryArg ?? {};

				const params = {
					PageNumber,
					RowsPerPage,
					...(searchText && { searchText }),
					...(clientId && { clientId }),
					...(ownerDocIds.length > 0 && { ownerDocIds }),
					...(typeof isPrimary === 'boolean' && { isPrimary }),
					...(brokerGroupId && { brokerGroupId }),
				};

				const response = await baseQuery({
					url: '/search',
					method: 'POST',
					body: params,
				});

				if (response.error) {
					console.error(
						'[searchContacts] Error retrieving contacts from Insight',
						response.error,
					);
					return {
						error: {
							status: response.error?.status,
							data: response.error?.data,
						},
					};
				}

				return { data: response.data };
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useListContactsQuery,
	useGetContactQuery,
	useLazyGetContactQuery,
	useCreateContactMutation,
	useUpdateContactMutation,
	useDeleteContactMutation,
	useSearchContactsInfiniteQuery,
} = contactsApi;
